import { useEffect, useState } from "react";
import PendingPaymentActions from "@/components/PendingPaymentActions";
import { Link, useParams, useNavigate } from "react-router-dom";
import PublicLayout from "@/layouts/PublicLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import Seo from "@/components/Seo";
import AddToCalendar from "@/components/AddToCalendar";
import SpeedMeetingBooking from "@/components/SpeedMeetingBooking";
import ExhibitorRegistrationModal from "@/components/ExhibitorRegistrationModal";
import { ShareEventDropdown } from "@/components/ShareEventDropdown";
import ParticipantDetailsModal from "@/components/ParticipantDetailsModal";
import { WT_TAG_CATEGORIES } from "@/lib/winetech_tags";
import GdprNotice from "@/components/GdprNotice";
import { MapPin, Calendar as Cal, Users, Wine, ArrowLeft, CalendarCheck, CheckCircle2, Languages, Check, Share2 } from "lucide-react";
import CountryFlag from "@/components/CountryFlag";
import { toast } from "sonner";
import CompanyLogo from "@/components/CompanyLogo";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("/api/")) ? `${BACKEND}${u}` : u;


export default function EventDetail() {
  const { slug } = useParams();
  const [e, setE] = useState(null);
  const [exhibitors, setExhibitors] = useState([]);
  const [participants, setParticipants] = useState({ visitors: [], exhibitors: [], media: [], total: 0 });
  const [agenda, setAgenda] = useState([]);
  const [participation, setParticipation] = useState("visitor");
  const [selectedTicketId, setSelectedTicketId] = useState("");
  // Sprint 92aj — Promo code live validation state.
  const [promoCode, setPromoCode] = useState("");
  const [promoResult, setPromoResult] = useState(null); // {valid, price, discount, ...} or {valid:false, error}
  const [promoChecking, setPromoChecking] = useState(false);
  const [selectedAddonIds, setSelectedAddonIds] = useState(new Set());
  const [smBooking, setSmBooking] = useState({ slotId: null, role: "" });
  const [smModal, setSmModal] = useState(null);
  const [exhRegSlot, setExhRegSlot] = useState(null);
  const [myReg, setMyReg] = useState(null); // current user's registration (or null)
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [openParticipant, setOpenParticipant] = useState(null);
  // Participant search & tag filter (applies to all 3 sections — visitors/exhibitors/media)
  const [participantQ, setParticipantQ] = useState("");
  const [participantTags, setParticipantTags] = useState({}); // { tag_<key>: [opt, ...] }
  const [showParticipantTagPanel, setShowParticipantTagPanel] = useState(false);
  const [participantSort, setParticipantSort] = useState("name");  // name | company | tag_count
  const { user } = useAuth();
  const { lang, t } = useLang();
  const nav = useNavigate();

  const ANIMATIONS = {
    round_table: { label: "Table ronde", emoji: "🎙️", color: "bg-blue-100 text-blue-700 border-blue-300" },
    conference: { label: "Conférence", emoji: "🎤", color: "bg-purple-100 text-purple-700 border-purple-300" },
    speed_meeting: { label: "RDV 1:1 individuel", emoji: "🤝", color: "bg-wine-100 text-wine-700 border-wine-300" },
    workshop: { label: "Atelier", emoji: "🛠️", color: "bg-amber-100 text-amber-700 border-amber-300" },
    demo: { label: "Démo", emoji: "💡", color: "bg-emerald-100 text-emerald-700 border-emerald-300" },
    cocktail: { label: "Cocktail", emoji: "🍷", color: "bg-pink-100 text-pink-700 border-pink-300" },
    informal: { label: "Échanges informels", emoji: "💬", color: "bg-neutral-100 text-neutral-700 border-neutral-300" },
  };

  // Group agenda by day for timeline display
  const agendaByDay = agenda.reduce((acc, s) => {
    const day = (s.start_time || "").slice(0, 10);
    (acc[day] = acc[day] || []).push(s);
    return acc;
  }, {});
  const dayKeys = Object.keys(agendaByDay).sort();

  useEffect(() => {
    api.get(`/events/${slug}`, { params: { lang } }).then((r) => {
      setE(r.data);
      api.get(`/events/${r.data.id}/exhibitors`).then((rx) => setExhibitors(rx.data));
      api.get(`/events/${r.data.id}/participants-public`).then((rp) => setParticipants(rp.data)).catch(() => {});
      api.get(`/events/${r.data.id}/agenda`).then((ra) => setAgenda(ra.data || []));
      if (user) {
        api.get(`/my/event-registration/${r.data.id}`)
          .then((rr) => { setMyReg(rr.data); if (rr.data && rr.data.status === "confirmed") setAlreadyRegistered(true); })
          .catch(() => setMyReg(null));
      } else {
        setMyReg(null);
      }
    }).catch(() => {});
  }, [slug, user, lang]);

  const cancelRegistration = async () => {
    try {
      const { data } = await api.delete(`/my/event-registration/${e.id}`);
      toast.success(data.cancelled_meetings > 0
        ? `Désinscription confirmée — ${data.cancelled_meetings} RDV annulé(s)`
        : "Désinscription confirmée");
      setMyReg(null);
      setCancelConfirm(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const bookSlot = async (slotId, role) => {
    if (!user) { toast.error("Connectez-vous pour réserver un créneau"); return; }
    try {
      await api.post(`/events/${e.id}/agenda/${slotId}/book`, { role });
      toast.success("Créneau réservé !");
      setSmBooking({ slotId: null, role: "" });
      api.get(`/events/${e.id}/agenda`).then((ra) => setAgenda(ra.data || []));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const [registering, setRegistering] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("payment") === "cancelled")
      toast.warning("Paiement non finalisé — vous pouvez payer à nouveau ou annuler votre inscription en attente ci-dessous.", { duration: 8000, id: "payment-cancelled" });
  }, []);
  const register = async () => {
    if (registering) return;
    // For events with custom tickets, require selection BEFORE opening any flow
    if ((e.tickets || []).length > 0 && !selectedTicketId) {
      toast.error("Veuillez sélectionner un type de billet ci-dessus");
      return;
    }
    if (!user) { setGuestOpen(true); return; }
    setRegistering(true);
    try {
      // Sprint 61: when a ticket is selected, derive participation_type from
      // the ticket's role — admin doesn't want users to be able to choose
      // Visiteur/Exposant/Media independently when a ticket dictates the role.
      const chosenTicket = (e.tickets || []).find((t) => t.id === selectedTicketId);
      const resolvedType = (chosenTicket && (chosenTicket.role || "")) || participation || "visitor";
      const body = { event_id: e.id, participation_type: resolvedType };
      if (selectedTicketId) body.ticket_id = selectedTicketId;
      if (promoCode && promoResult?.valid) body.promo_code = promoCode;
      if (selectedAddonIds.size > 0) body.addon_ids = Array.from(selectedAddonIds);
      const { data: reg } = await api.post("/event-registrations", body);
      if (reg.status === "pending_payment" && reg.price > 0) {
        // Trigger Stripe Checkout
        const { data: cs } = await api.post("/event-registrations/checkout", {
          registration_id: reg.id,
          origin_url: window.location.origin,
        });
        window.location.href = cs.checkout_url;
        return;
      }
      setAlreadyRegistered(true);
      toast.success("Inscription confirmée !");
      // Land on dedicated "my event" page with personalized next steps
      setTimeout(() => nav(`/portal/event/${e.slug}/overview`), 700);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setRegistering(false);
    }
  };

  // Pricing display helper
  const pricing = e?.pricing || {};
  const fmtPrice = (amt) => {
    if (!amt || amt <= 0) return "Gratuit";
    const cur = pricing.currency || "eur";
    const sym = { eur: "€", usd: "$", gbp: "£" }[cur] || cur.toUpperCase();
    return `${amt}${sym}`;
  };
  const optionPrice = (id) => {
    if (!pricing.is_paid) return "Gratuit";
    if (user?.role === "member" || user?.role === "admin") return fmtPrice(pricing.price_member);
    if (id === "visitor") return fmtPrice(pricing.price_visitor);
    if (id === "exhibitor") return fmtPrice(pricing.price_exhibitor);
    if (id === "media") return fmtPrice(pricing.price_media);
    return "Gratuit";
  };

  const [guestOpen, setGuestOpen] = useState(false);
  const [guestForm, setGuestForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", job_title: "" });
  const [guestResult, setGuestResult] = useState(null);

  const submitGuest = async (ge) => {
    ge.preventDefault();
    try {
      const body = { ...guestForm, event_id: e.id, participation_type: participation };
      if (selectedTicketId) body.ticket_id = selectedTicketId;
      if (promoCode && promoResult?.valid) body.promo_code = promoCode;
      if (selectedAddonIds.size > 0) body.addon_ids = Array.from(selectedAddonIds);
      const { data } = await api.post("/events/guest-register", body);
      if (data.checkout_url) {
        // Paid event → redirect to Stripe immediately
        toast.success("Inscription enregistrée — redirection vers le paiement…");
        setTimeout(() => { window.location.href = data.checkout_url; }, 400);
        return;
      }
      setGuestResult(data);
      toast.success(data.activation_email_sent
        ? "Inscription confirmée — vérifiez votre email pour activer votre compte"
        : "Inscription confirmée — votre badge est prêt !");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (!e) return <PublicLayout><div className="py-20 text-center text-neutral-500">{t("comp_eventdetail_90")}</div></PublicLayout>;

  return (
    <PublicLayout>
      <Seo
        title={e.title}
        description={e.description?.slice(0, 160)}
        path={`/events/${e.slug}`}
        image={e.image ? resolveUrl(e.image) : undefined}
        type="event"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Event",
          name: e.title,
          description: e.description,
          startDate: e.date,
          endDate: e.end_date || e.date,
          eventStatus: "https://schema.org/EventScheduled",
          eventAttendanceMode: e.format === "digital" ? "https://schema.org/OnlineEventAttendanceMode" : e.format === "hybrid" ? "https://schema.org/MixedEventAttendanceMode" : "https://schema.org/OfflineEventAttendanceMode",
          location: { "@type": "Place", name: e.location, address: { "@type": "PostalAddress", addressLocality: e.city, addressCountry: e.country } },
          image: e.image ? resolveUrl(e.image) : undefined,
          organizer: { "@type": "Organization", name: "La WineTech", url: "https://lawinetech.com" },
        }}
      />
      {user && (
        <div className="border-b border-wine-100 bg-wine-50/40">
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-6 py-2.5">
            <span className="text-xs font-medium text-neutral-600">{t("comp_eventdetail_91")} <span className="font-bold text-wine-700">{user.name}</span></span>
            <Link to={user.role === "admin" ? "/admin" : user.role === "journalist" ? "/press" : "/portal"} className="inline-flex items-center gap-1 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-semibold text-white hover:bg-wine-600" data-testid="event-back-to-portal">{t("comp_eventdetail_92")}</Link>
          </div>
        </div>
      )}
      <section className="mx-auto max-w-6xl px-6 py-10">
        <Link to="/events" className="inline-flex items-center gap-1.5 text-sm font-medium text-wine-600 hover:text-wine-700" data-testid="back-to-events">
          <ArrowLeft className="h-4 w-4" /> {t("back_to_events")}
        </Link>

        <div className="mt-6 grid gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <div className="relative">
              <img src={resolveUrl(e.image)} alt={e.title} className="aspect-[16/9] w-full rounded-3xl object-cover shadow-md" data-testid="event-detail-image" />
              {e.is_cancelled && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden rounded-3xl" data-testid="event-cancelled-watermark">
                  <div className="absolute inset-0 bg-black/40" />
                  <div className="-rotate-12 transform">
                    <div className="rounded-xl border-8 border-rose-600 bg-white/95 px-12 py-6 shadow-2xl">
                      <div className="text-5xl font-black uppercase tracking-widest text-rose-600">Annulé</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="rounded-full bg-wine-50 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-wine-700">{e.format}</span>
              <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-medium text-neutral-700">{e.type}</span>
            </div>
            <h1 className="mt-4 text-4xl font-bold text-navy md:text-5xl" data-testid="event-detail-title">{e.title}</h1>
            {e._translated && (
              <div className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs text-amber-700" data-testid="auto-translated-badge">
                <Languages className="h-3 w-3" />
                {t("auto_translated") || "Traduit automatiquement"}
              </div>
            )}
            <p className="mt-3 text-lg text-neutral-700">{e.description}</p>
            <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-neutral-700">
              <div className="inline-flex items-center gap-1.5"><Cal className="h-4 w-4 text-wine-600" /> {new Date(e.date).toLocaleDateString("fr", { dateStyle: "full" })}</div>
              <div className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-wine-600" /> {e.location}</div>
              {e.country && <CountryFlag country={e.country} size="sm" className="ml-1" />}
              <div className="inline-flex items-center gap-1.5"><Users className="h-4 w-4 text-wine-600" /> {Math.max(e.registered || 0, 0)}{e.max_participants ? ` / ${e.max_participants}` : ""}</div>
              {e.date && (
                <AddToCalendar
                  title={e.title}
                  description={e.description || e.long_description || ""}
                  location={[e.location, e.city, e.country].filter(Boolean).join(", ")}
                  startDate={e.date}
                  endDate={e.end_date}
                  virtualLink={e.virtual_link}
                  virtualPassword={e.virtual_password}
                  testId="event-add-to-calendar"
                />
              )}
              {/* Sprint 92y → 92ac — Multi-channel share dropdown.
                  - Plain-text messages (no emojis: WhatsApp/some clients
                    render them as � on older devices).
                  - Cache-buster ?v={updated_at} on the URL forces WhatsApp
                    to re-fetch fresh OG preview (image + title + desc).
                  - The <Seo> component already emits og:image from e.image. */}
              {(() => {
                const cacheBust = (e.updated_at || e.date || "").toString().slice(0, 10).replace(/[^0-9]/g, "");
                const eventUrl = `${window.location.origin}/events/${e.slug}${cacheBust ? `?v=${cacheBust}` : ""}`;
                // Sprint 92ac — Share URL points to /api/og/events/{slug} which
                // returns a proper OG preview page for social scrapers (bots
                // don't run JS so React Helmet's tags never reach them) and
                // 302-redirects real browsers to the SPA event page.
                const shareUrl = `${window.location.origin}/api/og/events/${e.slug}${cacheBust ? `?v=${cacheBust}` : ""}`;
                const dateLabel = e.date
                  ? new Date(e.date).toLocaleDateString(
                      lang === "en" ? "en-GB" : lang === "es" ? "es" : lang === "it" ? "it" : lang === "pt" ? "pt" : "fr",
                      { dateStyle: "long" }
                    )
                  : "";
                const place = [e.location, e.city, e.country].filter(Boolean).join(", ");
                const descSnippet = (e.description || "").replace(/\s+/g, " ").slice(0, 200);
                const MSG = {
                  fr: `${e.title}\n\nDate : ${dateLabel}${place ? `\nLieu : ${place}` : ""}\n\n${descSnippet}\n\nProgramme complet et inscription :\n${shareUrl}\n\n— La WineTech`,
                  en: `${e.title}\n\nDate: ${dateLabel}${place ? `\nLocation: ${place}` : ""}\n\n${descSnippet}\n\nFull program and registration:\n${shareUrl}\n\n— La WineTech`,
                  es: `${e.title}\n\nFecha: ${dateLabel}${place ? `\nLugar: ${place}` : ""}\n\n${descSnippet}\n\nPrograma completo e inscripción:\n${shareUrl}\n\n— La WineTech`,
                  it: `${e.title}\n\nData: ${dateLabel}${place ? `\nLuogo: ${place}` : ""}\n\n${descSnippet}\n\nProgramma completo e iscrizione:\n${shareUrl}\n\n— La WineTech`,
                  pt: `${e.title}\n\nData: ${dateLabel}${place ? `\nLocal: ${place}` : ""}\n\n${descSnippet}\n\nPrograma completo e inscrição:\n${shareUrl}\n\n— La WineTech`,
                };
                const message = MSG[lang] || MSG.fr;
                const emailSubject = { fr: `Événement La WineTech : ${e.title}`, en: `La WineTech event: ${e.title}`, es: `Evento La WineTech: ${e.title}`, it: `Evento La WineTech: ${e.title}`, pt: `Evento La WineTech: ${e.title}` }[lang] || `Événement La WineTech : ${e.title}`;
                const shareLabel = { fr: "Partager", en: "Share", es: "Compartir", it: "Condividi", pt: "Partilhar" }[lang] || "Partager";
                const copyLabel = { fr: "Copier le lien", en: "Copy link", es: "Copiar enlace", it: "Copia link", pt: "Copiar link" }[lang] || "Copier le lien";
                const copiedLabel = { fr: "Copié", en: "Copied", es: "Copiado", it: "Copiato", pt: "Copiado" }[lang] || "Copié";
                const twitterText = `${e.title}${place ? " · " + place : ""}`;
                return (
                  <ShareEventDropdown
                    label={shareLabel}
                    channels={{
                      whatsapp: `https://wa.me/?text=${encodeURIComponent(message)}`,
                      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
                      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(shareUrl)}`,
                      email: `mailto:?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(message)}`,
                    }}
                    copyText={eventUrl}
                    copyLabel={copyLabel}
                    copiedLabel={copiedLabel}
                  />
                );
              })()}
            </div>
            <div className="mt-8 prose prose-neutral max-w-none">
              <p className="leading-relaxed text-neutral-700">{e.long_description}</p>
            </div>

            {/* Public catalog download — always available for published events */}
            <div className="mt-8 flex items-center justify-between rounded-2xl border border-wine-200 bg-wine-50/40 px-5 py-4" data-testid="catalog-download-section">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-wine-700">{t("comp_eventdetail_93")}</div>
                <div className="mt-0.5 text-xs text-neutral-600">{t("comp_eventdetail_94")}</div>
              </div>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/events/${e.id}/catalog.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600 whitespace-nowrap" data-testid="download-catalog-public">
                Télécharger
              </a>
            </div>

            {agenda.length > 0 && (
              <div className="mt-10" data-testid="event-public-agenda">
                <div className="flex items-baseline justify-between flex-wrap gap-2">
                  <h2 className="font-serif text-3xl text-navy">Programme</h2>
                  <div className="text-xs text-neutral-500">{agenda.length} session{agenda.length > 1 ? "s" : ""} · {Object.entries(agenda.reduce((acc,s) => { acc[s.animation_type] = (acc[s.animation_type]||0)+1; return acc; }, {})).map(([k,v]) => `${(ANIMATIONS[k]||{emoji:""}).emoji} ${v}`).join(" · ")}</div>
                </div>

                {dayKeys.map((day) => (
                  <div key={day} className="mt-6">
                    <div className="sticky top-0 z-10 inline-flex items-center gap-2 rounded-full bg-wine-500 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-white shadow-md">
                      {new Date(day).toLocaleDateString("fr", { weekday: "long", day: "numeric", month: "long" })}
                    </div>
                    {/* Vertical timeline rail */}
                    <div className="relative mt-4 pl-6 sm:pl-10" data-testid={`timeline-day-${day}`}>
                      <div className="absolute left-2 top-2 bottom-2 w-0.5 bg-gradient-to-b from-wine-200 via-wine-300 to-wine-100 sm:left-4" aria-hidden="true" />
                      <div className="space-y-5">
                        {agendaByDay[day].sort((a,b) => (a.start_time||"").localeCompare(b.start_time||"")).map((s) => {
                          const a = ANIMATIONS[s.animation_type] || { label: s.animation_type, emoji: "•", color: "bg-neutral-100 text-neutral-700 border-neutral-300" };
                          const borderColor = a.color.split(' ').find(c => c.startsWith('border-')) || "border-neutral-300";
                          const start = new Date(s.start_time);
                          const end = new Date(s.end_time);
                          const isSpeedMeeting = s.animation_type === "speed_meeting";
                          const occupied = s.registered_count || 0;
                          const placesLeft = s.capacity > 0 ? Math.max(0, s.capacity - occupied) : null;
                          return (
                            <div key={s.id} className="relative" data-testid={`public-slot-${s.id}`}>
                              {/* Dot on the rail */}
                              <div className={`absolute -left-[18px] sm:-left-[26px] top-4 grid h-8 w-8 place-items-center rounded-full border-4 border-white shadow-md text-base ring-1 ring-wine-200 ${a.color}`}>
                                <span aria-hidden="true">{a.emoji}</span>
                              </div>
                              {/* Slot card */}
                              <div className={`flex gap-4 rounded-2xl border-l-4 bg-white p-5 transition-all hover:shadow-md ${borderColor}`}>
                                {/* Time column */}
                                <div className="hidden shrink-0 text-center sm:block min-w-[60px]">
                                  <div className="text-2xl font-bold text-navy">{start.toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}</div>
                                  <div className="text-[10px] uppercase text-neutral-500">{end.toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}</div>
                                </div>
                                {/* Body */}
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${a.color}`}>{a.emoji} {a.label}</span>
                                    {placesLeft !== null && (
                                      placesLeft > 0
                                        ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700">✓ {placesLeft}/{s.capacity} places</span>
                                        : <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">⚠ Complet</span>
                                    )}
                                    <span className="text-xs text-neutral-500 sm:hidden">{start.toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })} → {end.toLocaleTimeString("fr", { hour: "2-digit", minute: "2-digit" })}</span>
                                  </div>
                                  <h4 className="mt-2 font-serif text-xl font-bold text-navy">{s.title}</h4>
                                  {s.location && <div className="mt-1 text-xs text-neutral-600">📍 {s.location}</div>}
                                  {s.description && <p className="mt-2 text-sm text-neutral-700">{s.description}</p>}
                                  {isSpeedMeeting && (
                                    <div className="mt-3 rounded-xl bg-wine-50/50 px-3 py-2 text-xs">
                                      <div className="font-bold text-wine-700">🤝 RDV 1:1 individuel — toutes les {s.sm_slot_duration || 10} min</div>
                                      {s.sm_roles?.length > 0 && <div className="mt-1 text-neutral-600">Rôles : {s.sm_roles.map((r) => <span key={r} className="ml-1 rounded-full bg-white px-2 py-0.5 font-semibold text-wine-700 border border-wine-200">{r}</span>)}</div>}
                                    </div>
                                  )}
                                </div>
                                {/* Actions */}
                                <div className="hidden shrink-0 flex-col items-end gap-1.5 sm:flex">
                                  {isSpeedMeeting && user && (
                                    <>
                                      <button onClick={() => setSmModal(s)} className="rounded-full bg-wine-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-wine-600 whitespace-nowrap" data-testid={`sm-open-${s.id}`}>{t("comp_eventdetail_95")}</button>
                                      <button onClick={() => setExhRegSlot(s)} className="rounded-full border border-wine-300 bg-white px-4 py-1.5 text-[11px] font-semibold text-wine-700 hover:bg-wine-50 whitespace-nowrap" data-testid={`sm-become-exh-${s.id}`}>Devenir exposant</button>
                                    </>
                                  )}
                                  {isSpeedMeeting && !user && (
                                    <Link to="/login" className="rounded-full bg-wine-50 px-4 py-1.5 text-[11px] font-semibold text-wine-700 hover:bg-wine-100 whitespace-nowrap">Connectez-vous</Link>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {(participants.total > 0) && (
              <div className="mt-10" data-testid="event-public-participants">
                <div className="flex flex-wrap items-baseline justify-between gap-2">
                  <h2 className="font-serif text-2xl text-neutral-900">{t("comp_eventdetail_96")}</h2>
                  <div className="text-xs font-semibold uppercase tracking-wider text-wine-700">{participants.total} participant{participants.total > 1 ? "s" : ""} confirmé{participants.total > 1 ? "s" : ""}</div>
                </div>
                <p className="mt-1 text-sm text-neutral-600">{t("comp_eventdetail_97")}</p>

                {/* Search + tag filters + sort */}
                <ParticipantFilters
                  q={participantQ} onQ={setParticipantQ}
                  tags={participantTags} onTags={setParticipantTags}
                  sort={participantSort} onSort={setParticipantSort}
                  showPanel={showParticipantTagPanel} onToggle={() => setShowParticipantTagPanel((v) => !v)}
                  total={participants.total}
                />

                {(() => {
                  const filtered = {
                    exhibitors: filterAndSortParticipants(participants.exhibitors, participantQ, participantTags, participantSort),
                    visitors: filterAndSortParticipants(participants.visitors, participantQ, participantTags, participantSort),
                    media: filterAndSortParticipants(participants.media, participantQ, participantTags, participantSort),
                  };
                  const filteredTotal = filtered.exhibitors.length + filtered.visitors.length + filtered.media.length;
                  if (filteredTotal === 0) {
                    return (
                      <div className="mt-6 rounded-2xl border-2 border-dashed border-neutral-200 bg-neutral-50/50 p-8 text-center text-sm text-neutral-500" data-testid="participants-empty-filters">
                        Aucun participant ne correspond à votre recherche/filtres.
                      </div>
                    );
                  }
                  // Group by company so a company with N attendees renders a single card
                  // (the user wants one card per exposant/visiteur/media entity, not per person).
                  const groupByCompany = (list) => {
                    const buckets = new Map();
                    list.forEach((p) => {
                      const key = p.company_id || (p.company ? `name:${p.company.toLowerCase().trim()}` : `solo:${p.id}`);
                      if (!buckets.has(key)) buckets.set(key, { key, head: p, people: [] });
                      buckets.get(key).people.push(p);
                    });
                    return Array.from(buckets.values());
                  };
                  return (
                    <>
                      {filtered.exhibitors.length > 0 && (
                        <div className="mt-6" data-testid="participants-group-exhibitors">
                          <h3 className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-rose-700">🏢 Exposants <span className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px]">{filtered.exhibitors.length}</span></h3>
                          <div className="mt-3 flex flex-col gap-3">
                            {groupByCompany(filtered.exhibitors).map((g) => (
                              <CompanyParticipantCard key={g.key} group={g} variant="exhibitor" onOpen={(p) => setOpenParticipant(p)} />
                            ))}
                          </div>
                        </div>
                      )}

                      {filtered.visitors.length > 0 && (
                        <div className="mt-6" data-testid="participants-group-visitors">
                          <h3 className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-sky-700">👤 Visiteurs <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[10px]">{filtered.visitors.length}</span></h3>
                          <div className="mt-3 flex flex-col gap-3">
                            {groupByCompany(filtered.visitors).map((g) => (
                              <CompanyParticipantCard key={g.key} group={g} variant="visitor" onOpen={(p) => setOpenParticipant(p)} />
                            ))}
                          </div>
                        </div>
                      )}

                      {filtered.media.length > 0 && (
                        <div className="mt-6" data-testid="participants-group-media">
                          <h3 className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-violet-700">📰 Presse <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px]">{filtered.media.length}</span></h3>
                          <div className="mt-3 flex flex-col gap-3">
                            {groupByCompany(filtered.media).map((g) => (
                              <CompanyParticipantCard key={g.key} group={g} variant="media" onOpen={(p) => setOpenParticipant(p)} />
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}

            {exhibitors.length > 0 && (
              <div className="mt-10">
                <h2 className="font-serif text-2xl text-neutral-900">Exposants</h2>
                <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {exhibitors.map((ex) => (
                    <Link key={ex.id} to={`/portal/matchmaking/${ex.id}`} className="group rounded-2xl border border-neutral-100 bg-white p-4 hover:bg-wine-50/40" data-testid={`event-exhibitor-${ex.id}`}>
                      <div className="flex items-center gap-3">
                        <CompanyLogo src={ex.logo} name={ex.company_name} size={48} className="rounded-xl" />
                        <div>
                          <div className="font-serif text-base font-semibold text-neutral-900">{ex.company_name}</div>
                          <div className="text-xs text-neutral-500">Stand {ex.booth}</div>
                        </div>
                      </div>
                      <p className="mt-3 line-clamp-2 text-sm text-neutral-600">{ex.description}</p>
                      <div className="mt-3 text-xs font-semibold text-wine-600 group-hover:underline">Prendre rendez-vous →</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="rounded-2xl border border-wine-100 bg-gradient-to-br from-wine-50 to-white p-6">
              <div className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-wine-700">
                <Wine className="h-3.5 w-3.5" /> Inscription
              </div>
              <h3 className="mt-4 font-serif text-2xl text-neutral-900">
                {myReg && myReg.status === "confirmed" ? "Vous êtes inscrit·e ✓" : "S'inscrire à l'événement"}
              </h3>

              {e.company_indicator && e.company_indicator.colleagues_registered > 0 && (
                <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="company-indicator">
                  <div className="flex items-start gap-2">
                    <Users className="h-4 w-4 text-amber-700 shrink-0 mt-0.5" />
                    <div className="text-xs leading-relaxed text-amber-900">
                      <strong>{e.company_indicator.colleagues_registered}</strong> collaborateur{e.company_indicator.colleagues_registered > 1 ? "s" : ""} de <strong>{e.company_indicator.company_name}</strong> {e.company_indicator.colleagues_registered > 1 ? "sont déjà inscrits" : "est déjà inscrit"}.
                      {e.company_indicator.free_for_me && (
                        <div className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 font-bold text-emerald-800">
                          🎁 Inscription gratuite pour vous (licence partagée)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {myReg && myReg.status === "pending_payment" && (
                <div className="mt-4">
                  <PendingPaymentActions registration={myReg} eventId={e.id} onCancelled={() => { setMyReg(null); setAlreadyRegistered(false); }} />
                </div>
              )}
              {myReg && myReg.status === "confirmed" ? (
                <div className="mt-4" data-testid="already-registered-block">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <div className="text-sm font-bold text-navy">{t("comp_eventdetail_98")}</div>
                        <div className="mt-1 text-xs text-neutral-600">
                          <strong>Participation :</strong> {myReg.participation_type}<br />
                          {myReg.badge_code && <><strong>Badge :</strong> <span className="font-mono">{myReg.badge_code}</span></>}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 space-y-2">
                    <Link
                      to={`/portal/event/${e.slug}/overview`}
                      className="block w-full rounded-full bg-wine-500 px-6 py-3 text-center text-sm font-semibold text-white hover:bg-wine-600"
                      data-testid="goto-my-event"
                    >
                      Accéder à mon espace événement →
                    </Link>
                    {!cancelConfirm ? (
                      <button
                        onClick={() => setCancelConfirm(true)}
                        className="block w-full rounded-full border border-neutral-200 bg-white px-6 py-2 text-xs font-semibold text-neutral-600 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200"
                        data-testid="open-cancel-registration"
                      >
                        Se désinscrire
                      </button>
                    ) : (
                      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3" data-testid="cancel-confirm-box">
                        <div className="text-xs text-neutral-700">
                          <strong className="text-rose-700">{t("comp_eventdetail_99")}</strong><br />
                          Vos éventuels RDV 1:1 individuels seront annulés.
                          {myReg.payment_status === "paid" && myReg.price > 0 && (
                            <span className="mt-2 block rounded-md bg-amber-100 px-2 py-1 text-[11px] text-amber-800">
                              ⚠️ Vous avez payé {myReg.price}&nbsp;{(myReg.currency || "eur").toUpperCase()}. Aucun remboursement automatique — contactez l&apos;équipe.
                            </span>
                          )}
                        </div>
                        <div className="mt-3 flex gap-2">
                          <button onClick={cancelRegistration} className="flex-1 rounded-full bg-rose-500 px-4 py-1.5 text-xs font-bold text-white hover:bg-rose-600" data-testid="confirm-cancel">{t("comp_eventdetail_100")}</button>
                          <button onClick={() => setCancelConfirm(false)} className="flex-1 rounded-full bg-white border border-neutral-200 px-4 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="abort-cancel">{t("comp_eventdetail_101")}</button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="mt-4 space-y-2">
                    {/* Custom tickets — used if event has tickets[] defined */}
                    {(e.tickets || []).length > 0 ? (
                      (e.tickets || []).map((t) => {
                        const isSelected = selectedTicketId === t.id;
                        return (
                          <label
                            key={t.id}
                            className={`block cursor-pointer rounded-xl border-2 px-3 py-2.5 transition-all ${isSelected ? "border-wine-500 bg-white" : "border-transparent bg-white/60 hover:bg-white"}`}
                            data-testid={`event-ticket-${t.id}`}
                          >
                            <div className="flex items-start justify-between gap-2">
                              <div className="flex items-start gap-2 min-w-0">
                                <input type="radio" name="ticket" checked={isSelected} onChange={() => { setSelectedTicketId(t.id); setParticipation(t.role || "visitor"); }} className="mt-0.5 accent-wine-500 shrink-0" />
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-neutral-800 break-words">
                                    {t.name}
                                    {t.external_url && (
                                      <span className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[9px] font-bold text-indigo-700 align-middle" data-testid={`ticket-external-tag-${t.id}`}>🔗 externe</span>
                                    )}
                                  </div>
                                  {t.description && (
                                    <div className={`mt-0.5 text-[11px] text-neutral-500 ${isSelected ? "" : "line-clamp-2"}`} title={t.description}>{t.description}</div>
                                  )}
                                </div>
                              </div>
                              <span className="text-sm font-semibold text-wine-700 shrink-0">{t.price > 0 ? `${t.price} €` : "Gratuit"}</span>
                            </div>
                          </label>
                        );
                      })
                    ) : (
                      [
                        { id: "visitor", label: "Visiteur", price: optionPrice("visitor") },
                        { id: "exhibitor", label: "Exposant", price: optionPrice("exhibitor") },
                        { id: "media", label: "Média / Presse", price: optionPrice("media") },
                      ].map((opt) => (
                        <label
                          key={opt.id}
                          className={`flex cursor-pointer items-center justify-between rounded-xl border-2 px-4 py-3 transition-all ${participation === opt.id ? "border-wine-500 bg-white" : "border-transparent bg-white/60 hover:bg-white"}`}
                          data-testid={`event-participation-${opt.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <input type="radio" name="part" checked={participation === opt.id} onChange={() => setParticipation(opt.id)} className="accent-wine-500" />
                            <span className="text-sm font-medium text-neutral-800">{opt.label}</span>
                          </div>
                          <span className="text-sm font-semibold text-wine-700">{opt.price}</span>
                        </label>
                      ))
                    )}
                  </div>

                  {/* Sprint 75 — Add-on services (selected on top of the ticket) */}
                  {(e.add_on_services || []).length > 0 && (
                    <div className="mt-5 rounded-2xl border border-amber-200 bg-amber-50/40 p-4" data-testid="addons-section">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-bold text-navy">✨ Services additionnels</h4>
                        {selectedAddonIds.size > 0 && (
                          <span className="text-xs font-semibold text-amber-700">
                            +{(e.add_on_services || []).filter((a) => selectedAddonIds.has(a.id)).reduce((s, a) => s + (a.price || 0), 0).toFixed(0)} €
                          </span>
                        )}
                      </div>
                      <p className="mt-0.5 text-[11px] text-neutral-500">Sélectionnez les options à ajouter à votre billet</p>
                      <div className="mt-3 space-y-1.5">
                        {(e.add_on_services || []).map((a) => {
                          const isSel = selectedAddonIds.has(a.id);
                          return (
                            <label key={a.id} className={`flex cursor-pointer items-start justify-between gap-2 rounded-xl border-2 px-3 py-2 transition-all ${isSel ? "border-amber-400 bg-white" : "border-transparent bg-white/60 hover:bg-white"}`} data-testid={`addon-${a.id}`}>
                              <div className="flex items-start gap-2 min-w-0">
                                <input type="checkbox" checked={isSel} onChange={() => {
                                  const next = new Set(selectedAddonIds);
                                  if (next.has(a.id)) next.delete(a.id); else next.add(a.id);
                                  setSelectedAddonIds(next);
                                }} className="mt-0.5 accent-amber-500 shrink-0" data-testid={`addon-check-${a.id}`} />
                                <div className="min-w-0">
                                  <div className="text-sm font-semibold text-neutral-800 break-words">{a.name}</div>
                                  {a.description && <div className="mt-0.5 text-[11px] text-neutral-500" title={a.description}>{a.description}</div>}
                                </div>
                              </div>
                              <span className="text-sm font-semibold text-amber-700 shrink-0">{a.price > 0 ? `+${a.price} €` : "Inclus"}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {/* Sprint 92aj — Promo code input. Live validates on blur / Enter. */}
                  {(e.tickets || []).length > 0 && (
                    <div className="mt-4 rounded-2xl border border-pink-200 bg-pink-50/30 p-4" data-testid="promo-code-block">
                      <label className="text-xs font-bold uppercase tracking-wider text-pink-700">🏷️ Code promo</label>
                      <div className="mt-2 flex items-stretch gap-2">
                        <input
                          type="text"
                          value={promoCode}
                          onChange={(ev) => {
                            setPromoCode(ev.target.value.toUpperCase());
                            setPromoResult(null);
                          }}
                          onKeyDown={async (ev) => {
                            if (ev.key === "Enter") {
                              ev.preventDefault();
                              if (!promoCode || !selectedTicketId) return;
                              setPromoChecking(true);
                              try {
                                const { data } = await api.get(`/events/${e.id}/validate-promo`, { params: { code: promoCode, ticket_id: selectedTicketId } });
                                setPromoResult(data);
                              } catch { setPromoResult({ valid: false, error: "Code invalide" }); }
                              finally { setPromoChecking(false); }
                            }
                          }}
                          placeholder="EARLY20"
                          className="flex-1 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm font-mono"
                          data-testid="promo-code-input"
                        />
                        <button
                          type="button"
                          disabled={!promoCode || !selectedTicketId || promoChecking}
                          onClick={async () => {
                            setPromoChecking(true);
                            try {
                              const { data } = await api.get(`/events/${e.id}/validate-promo`, { params: { code: promoCode, ticket_id: selectedTicketId } });
                              setPromoResult(data);
                            } catch { setPromoResult({ valid: false, error: "Code invalide" }); }
                            finally { setPromoChecking(false); }
                          }}
                          className="rounded-xl bg-pink-600 px-4 py-2 text-xs font-bold text-white hover:bg-pink-700 disabled:opacity-50"
                          data-testid="promo-apply-btn"
                        >
                          {promoChecking ? "…" : "Appliquer"}
                        </button>
                      </div>
                      {promoResult && promoResult.valid && (
                        <div className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm" data-testid="promo-code-success">
                          <span className="font-semibold text-emerald-700">✓ Code appliqué — remise de {promoResult.discount} €</span>
                          <span className="text-right">
                            <span className="text-xs text-neutral-500 line-through">{promoResult.original_price}&nbsp;€</span>{" "}
                            <span className="font-bold text-emerald-700">{promoResult.price}&nbsp;€</span>
                          </span>
                        </div>
                      )}
                      {promoResult && !promoResult.valid && (
                        <div className="mt-2 rounded-xl bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700" data-testid="promo-code-error">
                          ✗ {promoResult.error || "Code promo invalide"}
                        </div>
                      )}
                      {!selectedTicketId && (
                        <p className="mt-2 text-[11px] text-neutral-500 italic">Sélectionnez d'abord un billet pour valider le code.</p>
                      )}
                    </div>
                  )}

                  {/* Sprint 92u — When the selected ticket has an external URL,
                      the CTA becomes a direct link to that URL instead of running
                      the internal registration flow. */}
                  {(() => {
                    const selectedTicket = (e.tickets || []).find((t) => t.id === selectedTicketId);
                    const externalUrl = selectedTicket?.external_url;
                    if (externalUrl) {
                      return (
                        <a
                          href={externalUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-indigo-700 hover:shadow-md"
                          data-testid="event-external-register-button"
                        >
                          🔗 Compléter mon inscription (formulaire externe) →
                        </a>
                      );
                    }
                    return (
                      <button
                        onClick={register}
                        disabled={alreadyRegistered || registering || ((e.tickets || []).length > 0 && !selectedTicketId)}
                        className="mt-5 w-full rounded-full bg-wine-500 px-6 py-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-wine-600 hover:shadow-md disabled:opacity-60 disabled:cursor-not-allowed"
                        data-testid="event-register-button"
                      >
                        {alreadyRegistered ? (
                          <><Check className="mr-1 inline h-4 w-4" /> Inscription enregistrée ✓</>
                        ) : registering ? (
                          <>Inscription en cours…</>
                        ) : (
                          <><CalendarCheck className="mr-1 inline h-4 w-4" /> Je m&apos;inscris</>
                        )}
                      </button>
                    );
                  })()}
                  {!user && <p className="mt-3 text-center text-xs text-neutral-500">{t("comp_eventdetail_102")} <a href="/login" className="font-semibold text-wine-700 underline">Se connecter</a></p>}
                  <div className="mt-4"><GdprNotice compact /></div>
                </>
              )}
            </div>

            {guestOpen && !user && (
              <div className="rounded-2xl border border-wine-200 bg-white p-5">
                {guestResult ? (
                  <div className="text-center" data-testid="guest-success">
                    <div className="grid h-14 w-14 mx-auto place-items-center rounded-full bg-emerald-100 mb-3">
                      <CheckCircle2 className="h-7 w-7 text-emerald-600" />
                    </div>
                    <h4 className="font-bold text-navy text-lg">{t("comp_eventdetail_103")}</h4>
                    <p className="mt-1 text-xs text-neutral-600">Badge n° <span className="font-mono font-bold text-wine-700">{guestResult.badge_code}</span></p>
                    {guestResult.activation_email_sent && (
                      <div className="mt-4 rounded-2xl border border-wine-100 bg-wine-50/40 p-3 text-left text-xs text-neutral-700">
                        <strong className="text-wine-700">📧 Email envoyé à {guestForm.email}</strong><br />
                        Cliquez sur le lien dans l&apos;email pour <strong>{t("comp_eventdetail_104")}</strong> et accéder à votre espace participant (badge, programme, RDV 1:1 individuel le cas échéant).
                      </div>
                    )}
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/registrations/${guestResult.registration_id}/badge`} target="_blank" rel="noreferrer" className="mt-3 inline-block rounded-full bg-wine-500 px-5 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="guest-badge-dl">{t("comp_eventdetail_105")}</a>
                  </div>
                ) : (
                  <form onSubmit={submitGuest} className="grid gap-2.5">
                    <h4 className="font-bold text-navy">{t("comp_eventdetail_106")}</h4>
                    {(() => {
                      const sel = (e.tickets || []).find((t) => t.id === selectedTicketId);
                      const price = sel ? sel.price : 0;
                      const cur = (e.pricing?.currency || "eur").toUpperCase();
                      if (price > 0) {
                        return (
                          <div className="rounded-lg border border-wine-200 bg-wine-50/60 p-2.5 text-xs">
                            <div className="font-semibold text-wine-800">Billet : {sel.name} — {price}{cur === "EUR" ? "€" : ` ${cur}`}</div>
                            <div className="mt-0.5 text-neutral-600">{t("comp_eventdetail_107")}</div>
                          </div>
                        );
                      }
                      if (sel) {
                        return <div className="rounded-lg bg-emerald-50/60 p-2.5 text-xs text-emerald-800">Billet : <strong>{sel.name}</strong> {t("comp_eventdetail_108")}</div>;
                      }
                      return null;
                    })()}
                    <div className="grid grid-cols-2 gap-2">
                      <input required value={guestForm.first_name} onChange={(ge) => setGuestForm({ ...guestForm, first_name: ge.target.value })} placeholder={t("comp_eventdetail_109")} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="guest-first-name" />
                      <input required value={guestForm.last_name} onChange={(ge) => setGuestForm({ ...guestForm, last_name: ge.target.value })} placeholder="Nom *" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="guest-last-name" />
                    </div>
                    <input required type="email" value={guestForm.email} onChange={(ge) => setGuestForm({ ...guestForm, email: ge.target.value })} placeholder="Email *" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="guest-email" />
                    <input value={guestForm.company} onChange={(ge) => setGuestForm({ ...guestForm, company: ge.target.value })} placeholder={t("comp_eventdetail_110")} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="guest-company" />
                    <input value={guestForm.job_title} onChange={(ge) => setGuestForm({ ...guestForm, job_title: ge.target.value })} placeholder="Fonction" className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="guest-job" />
                    <input value={guestForm.phone} onChange={(ge) => setGuestForm({ ...guestForm, phone: ge.target.value })} placeholder={t("comp_eventdetail_111")} className="rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="guest-phone" />
                    <p className="text-[11px] italic text-neutral-500">{t("comp_eventdetail_112")}</p>
                    <GdprNotice />
                    <button type="submit" className="rounded-full bg-wine-500 px-5 py-2 text-sm font-bold text-white hover:bg-wine-600" data-testid="guest-submit">
                      {(() => {
                        const sel = (e.tickets || []).find((t) => t.id === selectedTicketId);
                        return sel && sel.price > 0 ? "Continuer vers le paiement" : "Confirmer mon inscription";
                      })()}
                    </button>
                  </form>
                )}
              </div>
            )}
          </aside>
        </div>
      </section>
      {smModal && (
        <SpeedMeetingBooking
          eventId={e.id}
          slot={smModal}
          user={user}
          onClose={() => setSmModal(null)}
          onBooked={() => {}}
        />
      )}
      {exhRegSlot && (
        <ExhibitorRegistrationModal
          event={e}
          slot={exhRegSlot}
          user={user}
          onClose={() => setExhRegSlot(null)}
          onRegistered={() => {
            api.get(`/events/${e.id}/exhibitors`).then((rx) => setExhibitors(rx.data)).catch(() => {});
          }}
        />
      )}
      {openParticipant && (
        <ParticipantDetailsModal
          participant={openParticipant}
          eventId={e.id}
          canEdit={!!user && (user.role === "admin" || openParticipant.is_self || (openParticipant.company_id && user.company_id === openParticipant.company_id))}
          onClose={() => setOpenParticipant(null)}
          onSaved={() => {
            api.get(`/events/${e.id}/participants-public`).then((rp) => {
              setParticipants(rp.data);
              // Refresh the open card with latest values
              const all = [...(rp.data.exhibitors || []), ...(rp.data.visitors || []), ...(rp.data.media || [])];
              const fresh = all.find((x) => x.id === openParticipant.id);
              if (fresh) setOpenParticipant(fresh);
            }).catch(() => {});
          }}
        />
      )}
    </PublicLayout>
  );
}

// --- Sub-components (MUST be at module level to avoid React focus-loss bug) ---

function filterAndSortParticipants(list, q, tagFilters, sort) {
  let out = list || [];
  const qq = (q || "").trim().toLowerCase();
  if (qq) {
    out = out.filter((p) => {
      const hay = [
        p.first_name, p.last_name, p.company, p.job_title, p.description,
        (p.products || []).join(" "),
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(qq);
    });
  }
  // Tag filters: ALL selected tags must match
  const activeTagEntries = Object.entries(tagFilters || {}).filter(([, v]) => v && v.length > 0);
  if (activeTagEntries.length > 0) {
    out = out.filter((p) =>
      activeTagEntries.every(([field, opts]) =>
        opts.every((opt) => (p[field] || []).includes(opt))
      )
    );
  }
  // Sort
  const tagCount = (p) => WT_TAG_CATEGORIES.reduce((a, c) => a + (p[`tag_${c.key}`] || []).length, 0);
  if (sort === "company") {
    out = [...out].sort((a, b) => (a.company || "").localeCompare(b.company || "", "fr"));
  } else if (sort === "tag_count") {
    out = [...out].sort((a, b) => tagCount(b) - tagCount(a));
  } else { // name
    out = [...out].sort((a, b) => (a.first_name || "").localeCompare(b.first_name || "", "fr"));
  }
  return out;
}

function ParticipantFilters({ q, onQ, tags, onTags, sort, onSort, showPanel, onToggle, total }) {
  const { t } = useLang();
  const totalTags = Object.values(tags || {}).reduce((a, b) => a + (b?.length || 0), 0);
  const toggleTag = (key, opt) => {
    const cur = tags[key] || [];
    const next = cur.includes(opt) ? cur.filter((x) => x !== opt) : [...cur, opt];
    onTags({ ...tags, [key]: next });
  };
  return (
    <div className="mt-5 rounded-2xl border border-neutral-100 bg-white p-3" data-testid="participant-filters">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[220px] flex-1">
          <input
            value={q}
            onChange={(e) => onQ(e.target.value)}
            placeholder={t("comp_eventdetail_113")}
            className="w-full rounded-full border border-neutral-200 bg-wine-50/30 px-4 py-2 text-sm outline-none focus:border-wine-300"
            data-testid="participant-search-input"
          />
        </div>
        <select value={sort} onChange={(e) => onSort(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-xs font-semibold" data-testid="participant-sort">
          <option value="name">{t("comp_eventdetail_114")}</option>
          <option value="company">{t("comp_eventdetail_115")}</option>
          <option value="tag_count">{t("comp_eventdetail_116")}</option>
        </select>
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex items-center gap-1.5 rounded-full border border-wine-200 bg-wine-50/40 px-3 py-2 text-xs font-bold text-wine-700 hover:bg-wine-100"
          data-testid="participant-toggle-tags"
        >
          Tags {totalTags > 0 && <span className="rounded-full bg-wine-500 px-1.5 py-0.5 text-[10px] text-white">{totalTags}</span>}
          <span className="text-[10px]">{showPanel ? "−" : "+"}</span>
        </button>
        {totalTags > 0 && (
          <button type="button" onClick={() => onTags({})} className="rounded-full bg-neutral-100 px-3 py-2 text-xs font-semibold text-neutral-600 hover:bg-neutral-200" data-testid="participant-clear-tags">
            Effacer
          </button>
        )}
        <div className="ml-auto text-[11px] text-neutral-500">{total} participants au total</div>
      </div>
      {showPanel && (
        <div className="mt-3 space-y-3 border-t border-neutral-100 pt-3" data-testid="participant-tag-panel">
          {WT_TAG_CATEGORIES.map((cat) => {
            const selected = tags[`tag_${cat.key}`] || [];
            return (
              <div key={cat.key} data-testid={`participant-tag-cat-${cat.key}`}>
                <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-600">{cat.label}</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {cat.options.map((opt) => {
                    const isSel = selected.includes(opt);
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => toggleTag(`tag_${cat.key}`, opt)}
                        className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition ${
                          isSel
                            ? `${cat.badge} font-bold ring-1 ring-current`
                            : "border-neutral-200 bg-white text-neutral-600 hover:border-wine-300 hover:bg-wine-50/40"
                        }`}
                        data-testid={`participant-tag-${cat.key}-${opt}`}
                        aria-pressed={isSel}
                      >
                        {opt}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PARTICIPANT_VARIANTS = {
  exhibitor: { border: "border-rose-100", company: "text-wine-700", defaultColor: "#E33170" },
  visitor: { border: "border-sky-100", company: "text-neutral-700", defaultColor: "#263064" },
  media: { border: "border-violet-100", company: "text-neutral-700", defaultColor: "#7C3AED" },
};

function CompanyParticipantCard({ group, variant, onOpen }) {
  const v = PARTICIPANT_VARIANTS[variant] || PARTICIPANT_VARIANTS.visitor;
  const { head, people } = group;
  // Aggregate tags from ALL people in this company group (a company has shared tags,
  // but each individual registration can also carry personal tag overrides).
  const tagChips = [];
  const seen = new Set();
  people.forEach((p) => {
    WT_TAG_CATEGORIES.forEach((cat) => {
      (p[`tag_${cat.key}`] || []).forEach((opt) => {
        const key = `${cat.key}::${opt}`;
        if (!seen.has(key)) {
          seen.add(key);
          tagChips.push({ key: cat.key, badge: cat.badge, label: opt });
        }
      });
    });
  });
  const MAX_CHIPS = 8;
  const visibleChips = tagChips.slice(0, MAX_CHIPS);
  const moreChips = Math.max(0, tagChips.length - MAX_CHIPS);
  const hasInfo = people.some((p) => p.description || p.website || p.email || p.phone) || head.company;

  return (
    <div className={`group rounded-2xl border ${v.border} bg-white p-5 transition hover:border-wine-300 hover:shadow-md sm:p-6`} data-testid={`participant-company-${group.key}`}>
      <div className="flex items-start gap-4 sm:gap-5">
        <CompanyLogo src={head.company_logo} name={head.company || head.first_name} size={84} className="rounded-xl shrink-0" />
        <div className="min-w-0 flex-1">
          {/* Company name = primary title */}
          <div className={`text-lg font-bold sm:text-xl ${v.company}`} data-testid={`participant-company-${group.key}-name`}>
            {head.company || `${head.first_name || ""} ${head.last_name || ""}`.trim()}
          </div>
          {/* Ticket pill summary (distinct ticket names across the group) */}
          {(() => {
            const tickets = Array.from(new Set(people.map((p) => p.ticket_name).filter(Boolean)));
            return tickets.length > 0 ? (
              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                {tickets.map((t) => {
                  const matching = people.find((p) => p.ticket_name === t);
                  return (
                    <span key={t} className="inline-block rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ backgroundColor: matching?.ticket_color || v.defaultColor }}>
                      {t}
                    </span>
                  );
                })}
                <span className="text-xs text-neutral-500">· {people.length} personne{people.length > 1 ? "s" : ""}</span>
              </div>
            ) : null;
          })()}

          {/* Description: pick the first non-empty across the group */}
          {(() => {
            const desc = people.map((p) => p.description).find((d) => d && d.trim());
            return desc ? (
              <p className="mt-2.5 line-clamp-4 text-sm leading-relaxed text-neutral-700 sm:text-[15px]" data-testid={`participant-company-${group.key}-desc`}>
                {desc}
              </p>
            ) : null;
          })()}

          {tagChips.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`participant-company-${group.key}-tags`}>
              {visibleChips.map((chip, idx) => (
                <span key={idx} className={`rounded-full border ${chip.badge} px-2 py-0.5 text-[10px] font-semibold`}>
                  {chip.label}
                </span>
              ))}
              {moreChips > 0 && (
                <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-[10px] font-bold text-neutral-600">
                  +{moreChips}
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Attendees from this company */}
      <div className="mt-4 border-t border-neutral-100 pt-3">
        <div className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
          {people.length === 1 ? "Représenté par" : `${people.length} contacts présents`}
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          {people.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onOpen(p)}
              className="inline-flex items-center gap-2 rounded-full border border-neutral-200 bg-neutral-50/60 px-3 py-1.5 text-xs text-neutral-800 transition hover:border-wine-300 hover:bg-wine-50"
              data-testid={`participant-person-${p.id}`}
            >
              <span className="font-semibold">{p.first_name} {p.last_name}</span>
              {p.job_title && <span className="text-[11px] text-neutral-500">· {p.job_title}</span>}
            </button>
          ))}
        </div>
        {hasInfo && (
          <button
            type="button"
            onClick={() => onOpen(head)}
            className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-wine-600 hover:text-wine-700"
            data-testid={`participant-company-${group.key}-cta`}
          >
            Voir le profil détaillé →
          </button>
        )}
      </div>
    </div>
  );
}


